import React, { useState } from "react";
import "./EmailSubscriptionWidget.css";
import { trackEvent } from "@/utils/analytics/trackEvent";

const DEFAULT_EYEBROW = "UPDATES";
const DEFAULT_HEADLINE = "Stay in the loop.";
const DEFAULT_DESCRIPTION =
  "Field notes, maps, and adventures. We'll email you when it matters.";
const DEFAULT_SUCCESS = "You're subscribed. We'll be in touch.";
const DEFAULT_EMAIL_ARIA = "Email address for updates";
const DEFAULT_SUBMIT_LABEL = "Subscribe";

type EmailSubscriptionWidgetProps = {
  /** Stable prefix for heading and email field `id`s (one widget per page). */
  idPrefix?: string;
  eyebrow?: string;
  headline?: string;
  /** Plain string or rich text (e.g. line breaks). */
  description?: React.ReactNode;
  /** Shown after successful signup */
  successMessage?: string;
  /** `aria-label` on the email input */
  emailInputAriaLabel?: string;
  /** Submit button label (displayed uppercase via CSS) */
  submitButtonLabel?: string;
  /** Root element class(es) — e.g. modifier for layout-specific description rules */
  className?: string;
};

/**
 * Public mailing list signup — `POST /api/emails/list-subscribe` (Resend contact + confirmation email).
 * Full-bleed deep-forest band; place as last child in `PageLayout` main (with `useContainer={false}`).
 */
export const EmailSubscriptionWidget: React.FC<EmailSubscriptionWidgetProps> = ({
  idPrefix = "email-subscription",
  eyebrow = DEFAULT_EYEBROW,
  headline = DEFAULT_HEADLINE,
  description,
  successMessage = DEFAULT_SUCCESS,
  emailInputAriaLabel = DEFAULT_EMAIL_ARIA,
  submitButtonLabel = DEFAULT_SUBMIT_LABEL,
  className,
}) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headingId = `${idPrefix}-heading`;
  const emailInputId = `${idPrefix}-email`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch("/api/emails/list-subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: trimmed }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message =
          (data && typeof data.error === "string" && data.error) ||
          "Something went wrong. Please try again.";
        setError(message);
        return;
      }

      setSubmitted(true);
      trackEvent("email_subscribed", {
        page: typeof window === "undefined" ? undefined : window.location.pathname,
        location: idPrefix,
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const descriptionContent = description ?? DEFAULT_DESCRIPTION;
  const rootClass = ["email-subscription-widget", className]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={rootClass} aria-labelledby={headingId}>
      <div className="email-subscription-widget__inner">
        <p className="email-subscription-widget__eyebrow">{eyebrow}</p>
        <h2 id={headingId} className="email-subscription-widget__headline">
          {headline}
        </h2>
        <p className="email-subscription-widget__description">
          {descriptionContent}
        </p>
        {submitted ? (
          <p className="email-subscription-widget__success">{successMessage}</p>
        ) : (
          <form onSubmit={handleSubmit} className="email-subscription-widget__form">
            <div className="email-subscription-widget__form-row">
              <input
                id={emailInputId}
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="email-subscription-widget__input"
                aria-label={emailInputAriaLabel}
              />
              <button
                type="submit"
                className="email-subscription-widget__button"
                disabled={submitting}
              >
                {submitting ? "Sending…" : submitButtonLabel}
              </button>
            </div>
          </form>
        )}
        {error ? (
          <p className="email-subscription-widget__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default EmailSubscriptionWidget;
